import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { User, HelpCircle, Sparkles, Globe, ChevronDown, Menu, Code, History, Plus } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import MobileHamburgerMenu from './MobileHamburgerMenu';
import '../../css/components/FullChat/ChatHeader.css';

const ChatHeader = ({ onNewChat, onResetWelcome, user, isAdmin, onLogout, onLanguageClick, enableVoiceToVoice, onToggleVoiceToVoice, onStartProductTour, onHistoryClick, onMobileMenuChange }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { currentLanguage, changeLanguage, t } = useLanguage();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  const helpMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (helpMenuRef.current && !helpMenuRef.current.contains(event.target)) {
        setShowHelpMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProfileClick = () => {
    setShowProfileMenu(!showProfileMenu);
    setShowHelpMenu(false);
  };

  const handleHelpClick = () => {
    setShowHelpMenu(!showHelpMenu);
    setShowProfileMenu(false);
  };

  const handleProfileMenuClick = (action) => {
    setShowProfileMenu(false);
    if (action === 'profile') {
      navigate('/profile');
    } else if (action === 'logout') {
      if (onLogout) {
        onLogout();
      } else {
        // Fallback: clear localStorage and navigate
        localStorage.removeItem('user');
        navigate('/login');
      }
    }
  };

  const handleHelpMenuClick = (action) => {
    setShowHelpMenu(false);
    if (action === 'tour') {
      // This will be handled by the parent component
      window.dispatchEvent(new CustomEvent('startProductTour'));
    } else if (action === 'welcome') {
      // Trigger welcome popup
      if (onResetWelcome) {
        onResetWelcome();
      }
    }
  };


  const handleLanguageClick = (languageCode) => {
    changeLanguage(languageCode);
  };

  const handleMobileMenuToggle = () => {
    const next = !showMobileMenu;
    setShowMobileMenu(next);
    onMobileMenuChange?.(next);
  };

  const handleMobileMenuClose = () => {
    setShowMobileMenu(false);
    onMobileMenuChange?.(false);
  };

  return (
    <header className="chat-header" style={{
      background: theme.headerBg,
      borderBottom: `1px solid ${theme.headerBorder}`,
      padding: '16px 32px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      backdropFilter: 'blur(10px)',
      boxShadow: `0 2px 8px ${theme.headerShadow}`
    }}>
      {/* Left Section - Logo only; hamburger stays right via .header-right */}
      <div className="header-left" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
        flexShrink: 0
      }}>
        {/* Oakland AI Logo - Clickable */}
        <div
          className="header-logo"
          onClick={() => {
            if (onNewChat) {
              onNewChat();
            }
            navigate('/fullchat');
          }}
          style={{
            fontSize: '28px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '0.02em',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            padding: '4px 8px',
            borderRadius: '8px'
          }}
          title="Go to main page"
        >
          Oakland AI
        </div>


      </div>

      {/* Right Section - Help and Profile (hidden on mobile except New + hamburger via ChatHeader.css) */}
      <div className="header-right" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        {/* Mobile New Chat Button - visible only on mobile, top right */}
        <div className="mobile-new-chat-btn" style={{ display: 'none' }}>
          <button
            type="button"
            onClick={() => { if (onNewChat) onNewChat(); navigate('/fullchat'); }}
            className="mobile-new-chat-button"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '10px 14px',
              minHeight: '44px',
              height: '44px',
              background: theme.cardBg,
              border: `1px solid ${theme.primaryBorder}`,
              borderRadius: '12px',
              cursor: 'pointer',
              color: theme.primaryText,
              fontWeight: '600',
              fontSize: '13px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              whiteSpace: 'nowrap',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = theme.cardHoverBg || theme.cardBg;
              e.currentTarget.style.borderColor = theme.primary;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = theme.cardBg;
              e.currentTarget.style.borderColor = theme.primaryBorder;
            }}
            title={t('fullchat.newChat')}
          >
            <Plus size={18} strokeWidth={2.5} />
            <span>{t('fullchat.newChat')}</span>
          </button>
        </div>
        {/* Mobile Help (?) Button - visible only on mobile, order: logo | new chat | ? | menu */}
        <div className="mobile-help-btn" style={{ display: 'none' }}>
          <button
            type="button"
            onClick={() => onStartProductTour?.()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '44px',
              height: '44px',
              minWidth: '44px',
              minHeight: '44px',
              background: theme.cardBg,
              border: `1px solid ${theme.primaryBorder}`,
              borderRadius: '12px',
              cursor: 'pointer',
              color: theme.primaryText,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = theme.cardHoverBg;
              e.currentTarget.style.borderColor = theme.primary;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = theme.cardBg;
              e.currentTarget.style.borderColor = theme.primaryBorder;
            }}
            title={t('fullchat.helpAndTour') || 'Help'}
          >
            <HelpCircle size={20} />
          </button>
        </div>
        {/* Mobile Hamburger Menu Button - visible only on mobile */}
        <div className="mobile-hamburger-btn" style={{ display: 'none' }}>
          <button
            onClick={handleMobileMenuToggle}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '44px',
              height: '44px',
              background: theme.cardBg,
              border: `1px solid ${theme.primaryBorder}`,
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              color: theme.primaryText
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = theme.cardHoverBg;
              e.currentTarget.style.borderColor = theme.primary;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = theme.cardBg;
              e.currentTarget.style.borderColor = theme.primaryBorder;
            }}
            title="Menu"
          >
            <Menu size={20} />
          </button>
        </div>
        {/* Developer Button */}
        <button
          onClick={() => {
            // Check #3: CRITICAL - Verify DeveloperPage route exists before navigation
            try {
              // Verify the route is available
              const routes = window.location.pathname;
              navigate('/developer');
            } catch (error) {
              // CRITICAL ERROR: DeveloperPage route is missing
              console.error('❌ CRITICAL ERROR: Cannot navigate to /developer - DeveloperPage is required!', error);
              alert('CRITICAL ERROR: DeveloperPage is required but missing. The application cannot function without it. Please restore frontend/src/pages/DeveloperPage.jsx');
              throw new Error('DeveloperPage route is required but missing');
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: theme.helpButtonBg || theme.cardBg,
            borderRadius: '12px',
            border: `1px solid ${theme.helpButtonBorder || theme.primaryBorder}`,
            fontSize: '14px',
            fontWeight: '500',
            color: theme.helpButtonText || theme.secondaryText,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = theme.helpButtonHover || theme.cardHoverBg;
            e.currentTarget.style.borderColor = theme.primary;
            e.currentTarget.style.color = theme.primaryText;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = theme.helpButtonBg || theme.cardBg;
            e.currentTarget.style.borderColor = theme.helpButtonBorder || theme.primaryBorder;
            e.currentTarget.style.color = theme.helpButtonText || theme.secondaryText;
          }}
          title={t('navigation.developer')}
        >
          <Code size={16} />
            {t('navigation.developer')}
          </button>

        {/* Language Button */}
        <button
          onClick={() => setShowLanguageSelector(true)}
          className="language-button"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            background: theme.cardBg || theme.background || theme.secondaryBg || '#f8fafc',
            borderRadius: '8px',
            border: `1px solid ${theme.primaryBorder || theme.border || theme.cardBorder || theme.secondaryBorder || '#e2e8f0'}`,
            fontSize: '13px',
            fontWeight: '500',
            color: theme.secondaryText || theme.textSecondary || theme.text || theme.primaryText || '#64748b',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = theme.cardHoverBg || theme.background || theme.secondaryBg || '#f1f5f9';
            e.currentTarget.style.borderColor = theme.primary || theme.primaryColor || theme.accent || '#3b82f6';
            e.currentTarget.style.color = theme.primaryText || theme.text || theme.primaryText || '#1e293b';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = theme.cardBg || theme.background || theme.secondaryBg || '#f8fafc';
            e.currentTarget.style.borderColor = theme.primaryBorder || theme.border || theme.cardBorder || theme.secondaryBorder || '#e2e8f0';
            e.currentTarget.style.color = theme.secondaryText || theme.textSecondary || theme.text || theme.primaryText || '#64748b';
          }}
        >
          <Globe size={14} />
          <span>{(currentLanguage || 'en').toUpperCase()}</span>
          <ChevronDown size={12} />
        </button>

        {/* History Button */}
        <button
          onClick={onHistoryClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: theme.helpButtonBg || theme.cardBg,
            borderRadius: '12px',
            border: `1px solid ${theme.helpButtonBorder || theme.primaryBorder}`,
            fontSize: '14px',
            fontWeight: '500',
            color: theme.helpButtonText || theme.secondaryText,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = theme.helpButtonHover || theme.cardHoverBg;
            e.currentTarget.style.borderColor = theme.primary;
            e.currentTarget.style.color = theme.primaryText;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = theme.helpButtonBg || theme.cardBg;
            e.currentTarget.style.borderColor = theme.helpButtonBorder || theme.primaryBorder;
            e.currentTarget.style.color = theme.helpButtonText || theme.secondaryText;
          }}
          title="Chat History"
        >
          <History size={16} />
          {t('common.chatHistory')}
        </button>

        {/* Help Button */}
        <div ref={helpMenuRef} style={{ position: 'relative' }}>
          <button
            onClick={handleHelpClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              background: theme.helpButtonBg || theme.cardBg,
              borderRadius: '12px',
              border: `1px solid ${theme.helpButtonBorder || theme.primaryBorder}`,
              fontSize: '14px',
              fontWeight: '500',
              color: theme.helpButtonText || theme.secondaryText,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = theme.helpButtonHover || theme.cardHoverBg;
              e.currentTarget.style.borderColor = theme.primary;
              e.currentTarget.style.color = theme.primaryText;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = theme.helpButtonBg || theme.cardBg;
              e.currentTarget.style.borderColor = theme.helpButtonBorder || theme.primaryBorder;
              e.currentTarget.style.color = theme.helpButtonText || theme.secondaryText;
            }}
            title="Help & Support"
          >
            <HelpCircle size={16} />
{t('common.help')}
          </button>

          {/* Help Menu Dropdown */}
          {showHelpMenu && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              background: theme.modalBg,
              borderRadius: '12px',
              border: `1px solid ${theme.primaryBorder}`,
              boxShadow: `0 10px 25px ${theme.primaryShadow}`,
              minWidth: '200px',
              zIndex: 1001,
              overflow: 'hidden',
              animation: 'menuSlideDown 0.2s ease-out'
            }}>
              <button
                onClick={() => handleHelpMenuClick('tour')}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  color: theme.primaryText,
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                  fontSize: '14px',
                  fontWeight: '500',
                  textAlign: 'left',
                  justifyContent: 'flex-start'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = theme.cardHoverBg;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <HelpCircle size={16} />
                {t('common.productTour')}
              </button>
              <button
                onClick={() => handleHelpMenuClick('welcome')}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  color: theme.primaryText,
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                  fontSize: '14px',
                  fontWeight: '500',
                  textAlign: 'left',
                  justifyContent: 'flex-start'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = theme.cardHoverBg;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <Sparkles size={16} />
                {t('common.welcome')}
              </button>
            </div>
          )}
        </div>

        {/* Profile Button */}
        <div ref={profileMenuRef} style={{ position: 'relative' }}>

          <button
            onClick={handleProfileClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 16px',
              background: theme.cardBg,
              borderRadius: '12px',
              border: `1px solid ${theme.primaryBorder}`,
              fontSize: '14px',
              fontWeight: '500',
              color: theme.secondaryText,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = theme.cardHoverBg;
              e.currentTarget.style.borderColor = theme.primary;
              e.currentTarget.style.color = theme.primaryText;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = theme.cardBg;
              e.currentTarget.style.borderColor = theme.primaryBorder;
              e.currentTarget.style.color = theme.secondaryText;
            }}
            title="Profile & Settings"
          >
            <User size={16} />
{t('common.profile')}
          </button>

          {/* Profile Menu Dropdown */}
          {showProfileMenu && (
            <div 
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                background: theme.modalBg,
                borderRadius: '12px',
                border: `1px solid ${theme.primaryBorder}`,
                boxShadow: `0 10px 25px ${theme.primaryShadow}`,
                minWidth: '200px',
                zIndex: 1001,
                overflow: 'hidden',
                animation: 'menuSlideDown 0.2s ease-out'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => handleProfileMenuClick('profile')}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  color: theme.primaryText,
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                  fontSize: '14px',
                  fontWeight: '500',
                  textAlign: 'left',
                  justifyContent: 'flex-start'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = theme.cardHoverBg;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <User size={16} />
{t('common.viewProfile')}
              </button>
              <button
                onClick={() => handleProfileMenuClick('logout')}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  color: theme.error || '#ef4444',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                  fontSize: '14px',
                  fontWeight: '500',
                  textAlign: 'left',
                  justifyContent: 'flex-start'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = theme.errorBg || 'rgba(239, 68, 68, 0.1)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <User size={16} />
{t('common.logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Language Selector Modal */}
      <LanguageSelector
        isOpen={showLanguageSelector}
        onClose={() => setShowLanguageSelector(false)}
        onLanguageClick={handleLanguageClick}
      />

      {/* Mobile Hamburger Menu */}
      {showMobileMenu && (
        <MobileHamburgerMenu
          theme={theme}
          isAdmin={isAdmin}
          user={user}
          onLogout={onLogout}
          onNewChat={onNewChat}
          onLanguageClick={() => {
            setShowLanguageSelector(true);
            handleMobileMenuClose();
          }}
          enableVoiceToVoice={enableVoiceToVoice}
          onToggleVoiceToVoice={onToggleVoiceToVoice}
          onStartProductTour={onStartProductTour}
          onClose={handleMobileMenuClose}
        />
      )}
    </header>
  );
};

export default ChatHeader; 